import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Info } from 'lucide-react';
import { formatIndianCurrency, formatPreferCrores } from '../utils/currency';

interface AmountDisplayProps {
  amount: number;
  label?: string;
  textSize?: string;
  forceCrores?: boolean;
  /** Force side, or auto (flip based on space vs sticky header/footer) */
  placement?: 'top' | 'bottom' | 'auto';
}

export function AmountDisplay({
  amount,
  label = 'Exact Amount',
  textSize = 'text-2xl',
  forceCrores = false,
  placement = 'top',
}: AmountDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] = useState<'top' | 'bottom'>(
    placement === 'bottom' ? 'bottom' : 'top'
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return;

    if (placement === 'top' || placement === 'bottom') {
      setResolvedPlacement(placement);
      return;
    }

    const el = containerRef.current;
    const rect = el.getBoundingClientRect();
    const tooltipH = 80;
    const gap = 10;

    // Prefer space inside nearest scroll container (table max-h area) so sticky TOTAL is respected
    let parent: HTMLElement | null = el.parentElement;
    let scrollParent: HTMLElement | null = null;
    while (parent) {
      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) {
        scrollParent = parent;
        break;
      }
      parent = parent.parentElement;
    }

    const bounds = scrollParent?.getBoundingClientRect();
    const spaceAbove = bounds ? rect.top - bounds.top : rect.top;
    // Leave room for sticky TOTAL (~52px) when present
    const footerReserve = scrollParent?.querySelector('tfoot') ? 56 : 0;
    const spaceBelow = bounds
      ? bounds.bottom - rect.bottom - footerReserve
      : window.innerHeight - rect.bottom;

    if (spaceBelow >= tooltipH + gap) {
      setResolvedPlacement('bottom');
    } else if (spaceAbove >= tooltipH + gap) {
      setResolvedPlacement('top');
    } else {
      setResolvedPlacement(spaceBelow >= spaceAbove ? 'bottom' : 'top');
    }
  }, [isOpen, placement]);

  // forceCrores: show Cr when meaningful; tiny amounts stay as L/K/₹ so they don't become "0 Cr"
  const formattedValue = forceCrores ? formatPreferCrores(amount) : formatIndianCurrency(amount);

  return (
    <div
      className="relative inline-block"
      ref={containerRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div
        className="flex items-center justify-end space-x-0.5 mt-0.5 cursor-pointer group min-w-0"
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className={`${textSize} font-bold text-slate-800 truncate transition-colors group-hover:text-blue-600`}>
          ₹{formattedValue}
        </p>
        <div
          className={`p-0.5 rounded-full shrink-0 transition-colors ${isOpen ? 'bg-blue-50' : 'group-hover:bg-slate-100'}`}
        >
          <Info
            className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${isOpen ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`}
          />
        </div>
      </div>

      {isOpen && (
        <div
          className={`absolute right-0 p-3 bg-slate-800 text-white rounded-xl shadow-2xl z-[100] min-w-[160px] whitespace-nowrap animate-in fade-in zoom-in-95 duration-150 border border-slate-700 ${
            resolvedPlacement === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
          }`}
        >
          <p className="text-xs text-slate-300 font-medium mb-0.5">{label}</p>
          <p className="text-lg font-bold tracking-wide text-white">₹{amount.toLocaleString('en-IN')}</p>
          <div
            className={`absolute right-4 w-3 h-3 bg-slate-800 border-slate-700 transform rotate-45 ${
              resolvedPlacement === 'bottom' ? '-top-1.5 border-t border-l' : '-bottom-1.5 border-b border-r'
            }`}
          />
        </div>
      )}
    </div>
  );
}
